import { useUploader } from "./useUploader";
import { IngressListItemModel } from "components/layouts/elements/Header/IngressJobDropdownV2/lib/IngressListItemModel";
import { UploadStatus } from "lib/types/UploadStatus";

/**
 * Hook for managing upload operations (pause, resume, cancel)
 * This hook provides methods to control upload lifecycle for ingress items
 */
export const useUploadOperations = () => {
  const { pauseFile, resumeFile, cancelFile } = useUploader();

  /**
   * Pause an active upload
   * @param item - The ingress item to pause
   * @returns true if the operation was attempted, false if not applicable
   */
  const pauseUpload = (item: IngressListItemModel): boolean => {
    // Only pause uploads that are currently uploading or preparing
    if (
      item.ingressSource === "browserUpload" &&
      (item.uploadStatus === UploadStatus.uploading ||
        item.uploadStatus === UploadStatus.preparing)
    ) {
      pauseFile(item.id);
      return true;
    }
    return false;
  };

  /**
   * Resume a paused upload
   * @param item - The ingress item to resume
   * @returns true if the operation was attempted, false if not applicable
   */
  const resumeUpload = (item: IngressListItemModel): boolean => {
    // Only resume uploads that are paused
    if (
      item.ingressSource === "browserUpload" &&
      item.uploadStatus === UploadStatus.paused
    ) {
      resumeFile(item.id);
      return true;
    }
    return false;
  };

  /**
   * Cancel an upload
   * @param item - The ingress item to cancel
   * @returns true if the operation was attempted, false if not applicable
   */
  const cancelUpload = (item: IngressListItemModel): boolean => {
    // Cancel uploads that are in progress, paused, or queued
    if (
      item.ingressSource === "browserUpload" &&
      (item.uploadStatus === UploadStatus.uploading ||
        item.uploadStatus === UploadStatus.preparing ||
        item.uploadStatus === UploadStatus.paused ||
        item.uploadStatus === UploadStatus.queued)
    ) {
      cancelFile(item.id);
      return true;
    }
    return false;
  };

  /**
   * Check if an upload can be paused
   * @param item - The ingress item to check
   * @returns true if the upload can be paused
   */
  const canPause = (item: IngressListItemModel): boolean => {
    return (
      item.ingressSource === "browserUpload" &&
      (item.uploadStatus === UploadStatus.uploading ||
        item.uploadStatus === UploadStatus.preparing)
    );
  };

  /**
   * Check if an upload can be resumed
   * @param item - The ingress item to check
   * @returns true if the upload can be resumed
   */
  const canResume = (item: IngressListItemModel): boolean => {
    return (
      item.ingressSource === "browserUpload" &&
      item.uploadStatus === UploadStatus.paused
    );
  };

  /**
   * Check if an upload can be cancelled
   * @param item - The ingress item to check
   * @returns true if the upload can be cancelled
   */
  const canCancel = (item: IngressListItemModel): boolean => {
    return (
      item.ingressSource === "browserUpload" &&
      (item.uploadStatus === UploadStatus.uploading ||
        item.uploadStatus === UploadStatus.preparing ||
        item.uploadStatus === UploadStatus.paused ||
        item.uploadStatus === UploadStatus.queued)
    );
  };

  /**
   * Get the current upload operation status for an item
   * @param item - The ingress item to check
   * @returns object with available operations
   */
  const getUploadOperations = (item: IngressListItemModel) => {
    return {
      canPause: canPause(item),
      canResume: canResume(item),
      canCancel: canCancel(item),
      pause: () => pauseUpload(item),
      resume: () => resumeUpload(item),
      cancel: () => cancelUpload(item),
    };
  };

  return {
    pauseUpload,
    resumeUpload,
    cancelUpload,
    canPause,
    canResume,
    canCancel,
    getUploadOperations,
  };
};
